import { useState, createContext, PropsWithChildren } from 'react'
import { Optional } from 'shared/helpers/utilityTypes'

export type LayoutVariant =
  | 'full' // Static pages, for unlogged users.
  | 'sidebarFocused' // All form-filling experiences, such as the activity creation.
  | 'sidebarFull' // All  data-heavy pages, reports and visualisation pages.
  | 'calendar'
  | 'styleGuide'

type IsIncluded = { isIncluded?: boolean }

export type LayoutConfig = {
  layout: LayoutVariant
  sidebar?: IsIncluded
  header?: {
    /**
     * Using pageTitle instead of title to prevent the <header /> tag confusing it with the title attribute.
     */
    pageTitle?: string
    backButtonVisible?: boolean
  } & IsIncluded
  table?: IsIncluded
}

export type LayoutProvidersProps = PropsWithChildren<{
  initialLayout: Partial<LayoutConfig>
}>

export type LayoutConfigParams = Optional<LayoutConfig, 'layout'>
type SetLayoutType = (params?: LayoutConfigParams) => void

export interface LayoutContextProps extends LayoutConfig {
  /**
   * setLayout is just a function, it is not from React API.
   * In order to change state, and make React to be aware of its change, you need to use any setter function from React API.
   */
  setLayout: SetLayoutType
}

const DEFAULT_SETUP: LayoutConfig = {
  layout: 'sidebarFocused' as const,
  sidebar: {
    isIncluded: true
  },
  header: {
    isIncluded: true,
    backButtonVisible: false
  },
  table: {
    isIncluded: false
  }
}

export const LayoutContext = createContext<LayoutContextProps>({
  ...DEFAULT_SETUP,
  setLayout: () => {
    throw new Error('useLayout must be used within LayoutProvider')
  }
})

/**
 * It creates a context for the App to check the layout configuration such
 * showing sidebars and setting up the headers.
 */
const LayoutProvider = ({ initialLayout, children }: LayoutProvidersProps) => {
  const [layout, setLayoutState] = useState<LayoutConfig>(
    generateValues({ ...initialLayout })
  )

  const setLayout: SetLayoutType = params => {
    setLayoutState(generateValues(params))
  }

  return (
    <LayoutContext.Provider
      value={{
        ...layout,
        setLayout
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

export default LayoutProvider

const generateValues = (custom?: LayoutConfigParams) => ({
  // Use default values if some key is missing
  ...DEFAULT_SETUP,
  // Use JSON helpers to Remove undefined values
  ...JSON.parse(JSON.stringify(custom))
})
